(() => {
  const state = { heroTimers: new WeakMap(), heroTimelines: new WeakMap() };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionEnabled = () => document.body?.dataset.shrotaMotion !== 'false' && !reducedMotion();
  const staticPreview = new URLSearchParams(window.location.search).has('skipIntro');

  if (staticPreview) document.documentElement.classList.add('preview-static');

  function initPreloader() {
    const preloader = document.querySelector('[data-shrota-preloader]');
    if (!preloader || preloader.dataset.initialized === 'true') return;
    preloader.dataset.initialized = 'true';

    const bypass = staticPreview || Shopify.designMode || sessionStorage.getItem('shrota-seen') || !motionEnabled();
    if (bypass) {
      preloader.remove();
      return;
    }

    document.body.classList.add('is-locked');
    const finish = () => {
      sessionStorage.setItem('shrota-seen', 'true');
      document.body.classList.remove('is-locked');
      preloader.remove();
    };

    if (!window.gsap) {
      window.setTimeout(finish, 900);
      return;
    }

    const counter = preloader.querySelector('.preloader__counter');
    const timeline = window.gsap.timeline({ onComplete: finish });
    timeline
      .from(preloader.querySelectorAll('.preloader__letter'), {
        yPercent: 120,
        rotate: 6,
        stagger: 0.06,
        duration: 0.75,
        ease: 'power4.out',
      })
      .to(preloader.querySelector('.preloader__rule'), { scaleX: 1, duration: 0.7, ease: 'power3.inOut' }, '-=.25')
      .to(counter, { textContent: 100, duration: 0.65, snap: { textContent: 1 } }, '-=.65')
      .to(preloader.querySelector('.preloader__inner'), { yPercent: -12, opacity: 0, duration: 0.45, ease: 'power2.in' }, '+=.1')
      .to(preloader, { clipPath: 'inset(0 0 100% 0)', duration: 0.9, ease: 'power4.inOut' });
  }

  function initCursor() {
    const dot = document.querySelector('.cursor-dot');
    const ring = document.querySelector('.cursor-ring');
    if (!dot || !ring || dot.dataset.initialized === 'true' || !window.matchMedia('(pointer: fine)').matches) return;
    dot.dataset.initialized = 'true';

    if (!window.gsap || !motionEnabled()) {
      dot.hidden = true;
      ring.hidden = true;
      return;
    }

    const dotX = window.gsap.quickTo(dot, 'x', { duration: 0.18, ease: 'power3' });
    const dotY = window.gsap.quickTo(dot, 'y', { duration: 0.18, ease: 'power3' });
    const ringX = window.gsap.quickTo(ring, 'x', { duration: 0.45, ease: 'power3' });
    const ringY = window.gsap.quickTo(ring, 'y', { duration: 0.45, ease: 'power3' });

    window.addEventListener('pointermove', (event) => {
      dotX(event.clientX);
      dotY(event.clientY);
      ringX(event.clientX);
      ringY(event.clientY);
    });
    document.addEventListener('pointerover', (event) => {
      ring.classList.toggle('cursor-ring--active', Boolean(event.target.closest('a, button, input, textarea, select')));
    });
  }

  function initHeader(root = document) {
    root.querySelectorAll('[data-shrota-header]').forEach((header) => {
      if (header.dataset.initialized === 'true') return;
      header.dataset.initialized = 'true';
      const overlay = header.querySelector('[data-menu-overlay]');
      const open = header.querySelector('[data-menu-open]');
      const close = header.querySelector('[data-menu-close]');
      if (!overlay || !open || !close) return;

      const setOpen = (value) => {
        overlay.classList.toggle('menu-overlay--open', value);
        overlay.setAttribute('aria-hidden', String(!value));
        open.setAttribute('aria-expanded', String(value));
        document.body.classList.toggle('is-locked', value);
        (value ? close : open).focus({ preventScroll: true });
      };

      open.addEventListener('click', () => setOpen(true));
      close.addEventListener('click', () => setOpen(false));
      overlay.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
      overlay.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setOpen(false);
      });
    });
  }

  function splitArtistName(hero) {
    const name = hero.querySelector('[data-shrota-split]');
    if (!name || name.dataset.split === 'true') return;
    name.dataset.split = 'true';
    const label = name.textContent.trim();
    name.textContent = '';
    Array.from(label).forEach((letter) => {
      const span = document.createElement('span');
      span.textContent = letter === ' ' ? '\u00a0' : letter;
      name.appendChild(span);
    });
  }

  function initHero(root = document) {
    root.querySelectorAll('[data-shrota-hero]').forEach((hero) => {
      if (state.heroTimers.has(hero)) window.clearInterval(state.heroTimers.get(hero));
      if (state.heroTimelines.has(hero)) state.heroTimelines.get(hero).kill();
      splitArtistName(hero);

      const slides = Array.from(hero.querySelectorAll('[data-hero-slide]'));
      const dots = Array.from(hero.querySelectorAll('[data-hero-dot]'));
      const count = hero.querySelector('[data-hero-count]');
      if (!slides.length) return;
      let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let transitioning = false;
      let queued = null;

      const slideParts = (slide) => [
        slide?.querySelector('.cover-art'),
        slide?.querySelector('.hero-slide-copy'),
      ].filter(Boolean);

      const selectSlide = (index) => {
        slides.forEach((slide, slideIndex) => {
          const selected = slideIndex === index;
          slide.hidden = !selected;
          slide.classList.toggle('is-active', selected);
          if (window.gsap) window.gsap.set(slideParts(slide), { clearProps: 'opacity,transform' });
        });
        active = index;
      };

      const paint = (next, animate = true) => {
        next = (next + slides.length) % slides.length;
        if (transitioning) {
          queued = next;
          return;
        }
        if (next === active && hero.dataset.initialized === 'true') return;
        const previous = slides[active];
        const incoming = slides[next];
        const outgoingParts = slideParts(previous);
        const incomingParts = slideParts(incoming);

        incoming.hidden = false;
        incoming.classList.add('is-active');
        previous?.classList.remove('is-active');
        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === next);
          dot.setAttribute('aria-selected', String(index === next));
        });
        if (count) count.textContent = `${String(next + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
        active = next;

        if (!animate || !window.gsap || !motionEnabled()) {
          selectSlide(next);
          return;
        }

        transitioning = true;
        window.gsap.killTweensOf([...outgoingParts, ...incomingParts]);
        window.gsap.set(incomingParts, { y: 24, opacity: 0 });
        window.gsap.set(incoming.querySelector('.cover-art__wipe'), { scaleY: 0 });

        const finish = () => {
          selectSlide(next);
          transitioning = false;
          state.heroTimelines.delete(hero);
          if (queued !== null) {
            const queuedSlide = queued;
            queued = null;
            if (queuedSlide !== active) paint(queuedSlide);
          }
        };

        const timeline = window.gsap.timeline({ onComplete: finish });
        state.heroTimelines.set(hero, timeline);
        timeline
          .to(outgoingParts, { y: -16, opacity: 0, duration: 0.28, ease: 'power2.in' }, 0)
          .call(() => { if (previous) previous.hidden = true; }, null, 'handoff')
          .to(incomingParts, { y: 0, opacity: 1, duration: 0.68, ease: 'power3.out' }, 'handoff');
      };

      dots.forEach((dot, index) => dot.addEventListener('click', () => paint(index)));
      if (hero.dataset.initialized !== 'true' && window.gsap && motionEnabled()) {
        window.gsap.from(hero.querySelectorAll('.hero__name span'), { yPercent: 110, stagger: 0.06, duration: 1.15, delay: 0.15, ease: 'power4.out' });
        window.gsap.from(hero.querySelector('.hero__ambient'), { scale: 1.14, duration: 2.2, ease: 'power2.out' });
      }
      hero.dataset.initialized = 'true';
      selectSlide(active);

      if (hero.dataset.autoplay === 'true' && slides.length > 1 && motionEnabled() && !Shopify.designMode) {
        const interval = Number(hero.dataset.interval) || 6000;
        const timer = window.setInterval(() => paint(active + 1), interval);
        state.heroTimers.set(hero, timer);
      }
    });
  }

  function closeRelease(modal) {
    if (!modal) return;
    const finish = () => {
      modal.hidden = true;
      modal.classList.remove('is-open');
      document.body.classList.remove('is-locked');
    };
    if (window.gsap && motionEnabled()) window.gsap.to(modal, { opacity: 0, duration: 0.2, onComplete: finish });
    else finish();
  }

  function openRelease(modal) {
    if (!modal) return;
    modal.hidden = false;
    modal.classList.add('is-open');
    document.body.classList.add('is-locked');
    modal.querySelector('[data-release-close]')?.focus({ preventScroll: true });
    if (window.gsap && motionEnabled()) {
      window.gsap.fromTo(modal, { opacity: 0 }, { opacity: 1, duration: 0.25 });
      window.gsap.from(modal.querySelector('.release-modal__panel'), { yPercent: 100, duration: 0.7, ease: 'power4.out' });
      window.gsap.from(modal.querySelector('.release-modal__art > *'), { scale: 1.12, duration: 1, ease: 'power3.out' });
    }
  }

  function initMusic(root = document) {
    root.querySelectorAll('[data-shrota-music]').forEach((music) => {
      if (music.dataset.initialized === 'true') return;
      music.dataset.initialized = 'true';
      const cards = Array.from(music.querySelectorAll('[data-release-card]'));
      const filters = Array.from(music.querySelectorAll('[data-release-filter]'));
      const rail = music.querySelector('[data-release-rail]');

      ['album', 'ep', 'single'].forEach((type) => {
        const count = cards.filter((card) => card.dataset.releaseType === type).length;
        const output = music.querySelector(`[data-filter-count="${type}"]`);
        if (output) output.textContent = count;
      });

      filters.forEach((filter) => filter.addEventListener('click', () => {
        const value = filter.dataset.releaseFilter;
        filters.forEach((item) => item.classList.toggle('is-active', item === filter));
        cards.forEach((card) => { card.hidden = value !== 'all' && card.dataset.releaseType !== value; });
      }));

      music.querySelectorAll('[data-rail-direction]').forEach((control) => control.addEventListener('click', () => {
        rail?.scrollBy({ left: Number(control.dataset.railDirection) * Math.min(window.innerWidth * 0.68, 760), behavior: 'smooth' });
      }));

      cards.forEach((card) => card.addEventListener('click', () => openRelease(document.getElementById(card.dataset.releaseOpen))));
      music.querySelectorAll('[data-release-modal]').forEach((modal) => {
        modal.querySelectorAll('[data-release-close]').forEach((button) => button.addEventListener('click', () => closeRelease(modal)));
        modal.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeRelease(modal); });
      });
    });
  }

  function initProductCards(root = document) {
    root.querySelectorAll('[data-shrota-product-card]').forEach((card) => {
      if (card.dataset.initialized === 'true') return;
      card.dataset.initialized = 'true';
      const input = card.querySelector('input[name="id"]');
      const price = card.querySelector('[data-product-price]');
      const submit = card.querySelector('button[name="add"]');
      const label = card.querySelector('[data-add-label]');
      const choices = Array.from(card.querySelectorAll('[data-variant-choice]'));

      choices.forEach((choice) => choice.addEventListener('click', () => {
        const available = choice.dataset.variantAvailable === 'true';
        choices.forEach((item) => item.classList.toggle('is-active', item === choice));
        input.value = choice.dataset.variantId;
        input.disabled = !available;
        if (price) price.textContent = choice.dataset.variantPrice;
        submit.toggleAttribute('disabled', !available);
        submit.setAttribute('aria-disabled', String(!available));
        const store = submit.closest('[data-shrota-store]');
        if (label) label.textContent = available ? store?.dataset.addLabel || 'Add to bag' : store?.dataset.soldOutLabel || 'Sold out';
      }));
    });
  }

  function initReveals(root = document) {
    if (!window.gsap || !motionEnabled()) return;
    if (window.ScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);
    root.querySelectorAll('[data-shrota-reveal]:not([data-reveal-ready])').forEach((element) => {
      element.dataset.revealReady = 'true';
      window.gsap.from(element.children, {
        opacity: 0,
        y: 42,
        stagger: 0.08,
        duration: 0.85,
        ease: 'power3.out',
        scrollTrigger: window.ScrollTrigger ? { trigger: element, start: 'top 86%' } : undefined,
      });
    });
    root.querySelectorAll('[data-reveal-product]:not([data-reveal-ready])').forEach((element) => {
      element.dataset.revealReady = 'true';
      window.gsap.from(element, {
        opacity: 0,
        y: 80,
        duration: 0.95,
        ease: 'power3.out',
        scrollTrigger: window.ScrollTrigger ? { trigger: element, start: 'top 88%' } : undefined,
      });
    });
  }

  function init(root = document) {
    initPreloader();
    initCursor();
    initHeader(root);
    initHero(root);
    initMusic(root);
    initProductCards(root);
    initReveals(root);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-hero-slide]');
    if (!slide) return;
    const hero = slide.closest('[data-shrota-hero]');
    const index = Number(slide.dataset.heroSlide);
    hero?.querySelector(`[data-hero-dot="${index}"]`)?.click();
  });
})();
